import React, { useState } from 'react';

/**
 * A button component that creates sparkle effects when clicked.
 * 
 * @param {function} onClick - Click handler function
 * @param {React.ReactNode} children - Button content
 * @param {object} props - Additional button props
 */
const SparkleButton = ({ onClick, children, ...props }) => {
  const [sparkles, setSparkles] = useState([]);

  const createSparkle = (x, y) => ({
    id: Date.now() + Math.random(),
    x,
    y,
    angle: Math.random() * 360,
    travel: 10 + Math.random() * 35,
  });

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Create just 3 sparkles for a cleaner effect
    const newSparkles = Array.from({ length: 3 }).map(() => createSparkle(x, y));
    setSparkles(prev => [...prev, ...newSparkles]);

    if (onClick) onClick(e);
  };

  const handleAnimationEnd = (id) => {
    setSparkles(prev => prev.filter(s => s.id !== id));
  };

  return (
    <button
      {...props}
      onClick={handleClick}
      className={`relative overflow-visible ${props.className || ''}`}
    >
      {/* Sparkle animation styles */}
      <style jsx>{`
        .sparkle {
          position: absolute;
          width: 10px;
          height: 10px;
          background-image: radial-gradient(circle, white 0%, transparent 70%);
          border-radius: 50%;
          opacity: 0;
          pointer-events: none;
          animation: sparkle-out 700ms ease forwards;
          transform-origin: center center;
        }
        @keyframes sparkle-out {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: scale(1.5) rotate(var(--angle)) translate(var(--travel));
            opacity: 0;
          }
        }
      `}</style>
      
      {children}
      
      {sparkles.map(sparkle => (
        <div
          key={sparkle.id}
          className="sparkle"
          style={{
            left: sparkle.x,
            top: sparkle.y,
            '--angle': `${sparkle.angle}deg`,
            '--travel': `${sparkle.travel}px`,
          }}
          onAnimationEnd={() => handleAnimationEnd(sparkle.id)}
        />
      ))}
    </button>
  );
};

export default React.memo(SparkleButton);